import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import simulationService from "./simulationService";

const initialState = {
  catalog: [],
  simulation: null, // config: company, people, theme, columns, labels
  board: null, // { columns, cards }
  card: null, // { card, state, thread }
  wikiPages: [],
  wikiPage: null,

  isLoadingCatalog: false,
  isLoadingSimulation: false,
  isLoadingBoard: false,
  isLoadingCard: false,
  isLoadingWiki: false,

  // QA (async-shaped) per open ticket.
  qa: { submitting: false, status: null, verdict: null, message: null },

  isPostingComment: false,
  isRequestingCredentials: false,
  // The freshly minted secret, shown once right after a credentials request.
  freshCredentials: null,

  error: null,
  sessionExpired: false,
};

// Generic thunk wrapper: maps a 401 to a sessionExpired flag (matches cohort).
const wrap = (fn) => async (arg, thunkAPI) => {
  try {
    return await fn(arg);
  } catch (error) {
    if (error.response?.status === 401) {
      return thunkAPI.rejectWithValue({ sessionExpired: true });
    }
    return thunkAPI.rejectWithValue(
      error.response?.data?.message || error.message
    );
  }
};

export const getCatalog = createAsyncThunk(
  "simulation/getCatalog",
  wrap(() => simulationService.getCatalog())
);

export const getSimulation = createAsyncThunk(
  "simulation/getSimulation",
  wrap((slug) => simulationService.getSimulation(slug))
);

export const getBoard = createAsyncThunk(
  "simulation/getBoard",
  wrap((slug) => simulationService.getBoard(slug))
);

export const getCard = createAsyncThunk(
  "simulation/getCard",
  wrap(({ slug, cardKey }) => simulationService.getCard(slug, cardKey))
);

export const startCard = createAsyncThunk(
  "simulation/startCard",
  wrap(({ slug, cardKey }) => simulationService.startCard(slug, cardKey))
);

export const resolveCard = createAsyncThunk(
  "simulation/resolveCard",
  wrap(({ slug, cardKey }) => simulationService.resolveCard(slug, cardKey))
);

export const submitQa = createAsyncThunk(
  "simulation/submitQa",
  wrap(({ slug, cardKey }) => simulationService.submitQa(slug, cardKey))
);

export const pollQaStatus = createAsyncThunk(
  "simulation/pollQaStatus",
  wrap(({ slug, cardKey }) => simulationService.getQaStatus(slug, cardKey))
);

export const postComment = createAsyncThunk(
  "simulation/postComment",
  wrap(({ slug, cardKey, body }) =>
    simulationService.postComment(slug, cardKey, body)
  )
);

export const requestCredentials = createAsyncThunk(
  "simulation/requestCredentials",
  wrap(({ slug, cardKey }) =>
    simulationService.requestCredentials(slug, cardKey)
  )
);

export const getWiki = createAsyncThunk(
  "simulation/getWiki",
  wrap((slug) => simulationService.getWiki(slug))
);

export const getWikiPage = createAsyncThunk(
  "simulation/getWikiPage",
  wrap(({ slug, pageSlug }) => simulationService.getWikiPage(slug, pageSlug))
);

const simulationSlice = createSlice({
  name: "simulation",
  initialState,
  reducers: {
    clearCard(state) {
      state.card = null;
      state.qa = { submitting: false, status: null, verdict: null, message: null };
      state.freshCredentials = null;
    },
    clearWikiPage(state) {
      state.wikiPage = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // Catalog
      .addCase(getCatalog.pending, (s) => {
        s.isLoadingCatalog = true;
      })
      .addCase(getCatalog.fulfilled, (s, a) => {
        s.isLoadingCatalog = false;
        s.catalog = a.payload;
      })
      .addCase(getCatalog.rejected, (s) => {
        s.isLoadingCatalog = false;
      })

      // Simulation config
      .addCase(getSimulation.pending, (s) => {
        s.isLoadingSimulation = true;
      })
      .addCase(getSimulation.fulfilled, (s, a) => {
        s.isLoadingSimulation = false;
        s.simulation = a.payload;
      })
      .addCase(getSimulation.rejected, (s) => {
        s.isLoadingSimulation = false;
      })

      // Board
      .addCase(getBoard.pending, (s) => {
        s.isLoadingBoard = true;
      })
      .addCase(getBoard.fulfilled, (s, a) => {
        s.isLoadingBoard = false;
        s.board = a.payload;
        s.sessionExpired = false;
      })
      .addCase(getBoard.rejected, (s, a) => {
        s.isLoadingBoard = false;
        s.sessionExpired = !!a.payload?.sessionExpired;
      })

      // Card / ticket
      .addCase(getCard.pending, (s) => {
        s.isLoadingCard = true;
      })
      .addCase(getCard.fulfilled, (s, a) => {
        s.isLoadingCard = false;
        s.card = a.payload;
        s.qa.status = a.payload?.state?.status || null;
        s.qa.verdict = a.payload?.state?.lastQaVerdict || null;
        s.sessionExpired = false;
      })
      .addCase(getCard.rejected, (s, a) => {
        s.isLoadingCard = false;
        s.sessionExpired = !!a.payload?.sessionExpired;
      })

      // State transitions refresh the open card's state.
      .addCase(startCard.fulfilled, (s, a) => {
        if (s.card) s.card.state = a.payload;
      })
      .addCase(resolveCard.fulfilled, (s, a) => {
        if (s.card) s.card.state = a.payload;
      })

      // QA submit
      .addCase(submitQa.pending, (s) => {
        s.qa.submitting = true;
        s.qa.status = "qa";
        s.qa.verdict = null;
        s.qa.message = null;
      })
      .addCase(submitQa.fulfilled, (s, a) => {
        s.qa.submitting = false;
        s.qa.status = a.payload.status;
        s.qa.verdict = a.payload.verdict;
        s.qa.message = a.payload.message;
      })
      .addCase(submitQa.rejected, (s, a) => {
        s.qa.submitting = false;
        s.sessionExpired = !!a.payload?.sessionExpired;
      })
      .addCase(pollQaStatus.fulfilled, (s, a) => {
        s.qa.status = a.payload.status;
        s.qa.verdict = a.payload.lastQaVerdict;
        if (s.card) s.card.thread = a.payload.thread;
      })

      // Comments
      .addCase(postComment.pending, (s) => {
        s.isPostingComment = true;
      })
      .addCase(postComment.fulfilled, (s, a) => {
        s.isPostingComment = false;
        if (s.card) s.card.thread = [...(s.card.thread || []), a.payload];
      })
      .addCase(postComment.rejected, (s) => {
        s.isPostingComment = false;
      })

      // Credentials exchange
      .addCase(requestCredentials.pending, (s) => {
        s.isRequestingCredentials = true;
      })
      .addCase(requestCredentials.fulfilled, (s, a) => {
        s.isRequestingCredentials = false;
        s.freshCredentials = a.payload.credentials;
        if (s.card) s.card.thread = a.payload.thread;
      })
      .addCase(requestCredentials.rejected, (s) => {
        s.isRequestingCredentials = false;
      })

      // Wiki
      .addCase(getWiki.pending, (s) => {
        s.isLoadingWiki = true;
      })
      .addCase(getWiki.fulfilled, (s, a) => {
        s.isLoadingWiki = false;
        s.wikiPages = a.payload;
      })
      .addCase(getWiki.rejected, (s) => {
        s.isLoadingWiki = false;
      })
      .addCase(getWikiPage.fulfilled, (s, a) => {
        s.wikiPage = a.payload;
      });
  },
});

export const { clearCard, clearWikiPage } = simulationSlice.actions;
export default simulationSlice.reducer;
